import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	OutreachAnswer,
	OutreachApproval,
	OutreachExclusionFilter,
	OutreachReachedKind,
	type OutreachReachedPerson,
	type OutreachScoredPerson,
	type OutreachSentBatch,
	type OutreachSignUp,
	OutreachVia,
	type RequestOutreach,
} from "../../../utils/models";
import { isRecommendedSignUp } from "../../../utils/outreachRules";
import {
	isCalling,
	type OutreachGroupHelpers,
	OutreachGroupIcon,
	type OutreachGroupView,
	OutreachRowAction,
	type OutreachRowBadge,
	type OutreachRowView,
	OutreachTab,
} from "./outreachGroupTypes";
import { upNextGroupsOf } from "./outreachUpNextGroups";
import { dayMetaOf, type OutreachCopy, TIME_FORMAT } from "./useOutreachCopy";

const VIA_ICON: Record<OutreachVia, OutreachGroupIcon> = {
	[OutreachVia.Text]: OutreachGroupIcon.Message,
	[OutreachVia.AppAlert]: OutreachGroupIcon.AppAlert,
	[OutreachVia.Call]: OutreachGroupIcon.Call,
};

/** A send time on its row: the group's own meta already names the day, so a later day only needs its weekday. */
const ROW_DAY_FORMAT = "ccc";

/** "Sent by you" reads "Just now" for this long after the latest manual send. */
const JUST_NOW_MINUTES = 2;

const isOvertimeOffer = (entry: OutreachReachedPerson): boolean =>
	entry.kind === OutreachReachedKind.Override && entry.filter === OutreachExclusionFilter.DailyOvertime;

/** A sign-up's standing as row badges: waiting ones say whether we recommend approving them. */
const signUpBadgesOf = (t: TFunction, signUp: OutreachSignUp): OutreachRowBadge[] => {
	switch (signUp.approval) {
		case OutreachApproval.Waiting: {
			const awaitingApproval: OutreachRowBadge = {
				id: "awaiting-approval",
				label: t("request.outreach.badge.awaiting_approval"),
				tone: "warning",
				solid: false,
			};
			const recommend: OutreachRowBadge = { id: "recommend", label: t("request.outreach.badge.recommend"), tone: "success", solid: false };
			return isRecommendedSignUp(signUp.check) ? [recommend, awaitingApproval] : [awaitingApproval];
		}
		case OutreachApproval.ByManager:
			return [{ id: "approved", label: t("request.outreach.badge.approved"), tone: "success", solid: true }];
		case OutreachApproval.Automatic:
			return [{ id: "approved", label: t("request.outreach.badge.auto_approved"), tone: "success", solid: true }];
		case OutreachApproval.NotApproved:
			return [{ id: "not-approved", label: t("request.outreach.badge.not_approved"), tone: "neutral", solid: false }];
	}
};

/** What became of one send: a sign-up first, then a manual send's channel, then how they answered. */
const reachedBadgesOf = (t: TFunction, entry: OutreachReachedPerson, byUser: boolean, signUp: OutreachSignUp | undefined): OutreachRowBadge[] => {
	if (signUp !== undefined) return signUpBadgesOf(t, signUp);
	// An app alert is a broadcast, not a question to them, so it carries no answer of its own.
	if (entry.via === OutreachVia.AppAlert) return [];
	if (byUser) {
		const sent = isOvertimeOffer(entry) ? "overtime" : entry.via;
		return [{ id: "sent", label: t(`request.outreach.badge.${sent}_by_you`), tone: "primary", solid: false }];
	}
	return entry.answer === OutreachAnswer.Declined
		? [{ id: "declined", label: t("request.outreach.badge.declined"), tone: "error", solid: false }]
		: [{ id: "no-answer", label: t("request.outreach.badge.no_answer"), tone: "neutral", solid: false }];
};

/** Pending approvals, approved pickups, and declined offers after a group's title. */
const answersSummaryOf = (t: TFunction, entries: readonly OutreachReachedPerson[], signUpOf: (nurseId: number) => OutreachSignUp | undefined): string => {
	const waiting = entries.filter((entry) => signUpOf(entry.person.nurseId)?.approval === OutreachApproval.Waiting).length;
	const approved = entries.filter((entry) => {
		const approval = signUpOf(entry.person.nurseId)?.approval;
		return approval === OutreachApproval.ByManager || approval === OutreachApproval.Automatic;
	}).length;
	const declined = entries.filter(
		(entry) => entry.via !== OutreachVia.AppAlert && signUpOf(entry.person.nurseId) === undefined && entry.answer === OutreachAnswer.Declined,
	).length;
	return [
		...(waiting > 0 ? [t("request.outreach.answers.awaiting_approval", { count: waiting })] : []),
		...(approved > 0 ? [t("request.outreach.answers.approved", { count: approved })] : []),
		...(declined > 0 ? [t("request.outreach.answers.declined", { count: declined })] : []),
	]
		.map((part) => ` · ${part}`)
		.join("");
};

/**
 * The plan's sends as Reached shows them: each text batch, app alert and overtime offer on its own, and the calls one
 * group per clock hour (they're made one by one, every few minutes), dated by the hour's first call. Newest first.
 */
const byHourOfCalls = (batches: readonly OutreachSentBatch[]): OutreachSentBatch[] => {
	const out: OutreachSentBatch[] = [];
	const hours = new Map<number, OutreachSentBatch>();
	for (const batch of batches) {
		if (batch.people[0].via !== OutreachVia.Call || isOvertimeOffer(batch.people[0])) {
			out.push(batch);
			continue;
		}
		const hour = batch.sentAt.startOf("hour").toMillis();
		const group = hours.get(hour);
		if (group === undefined) {
			const created = { ...batch, id: `calls-${hour}`, people: [...batch.people] };
			hours.set(hour, created);
			out.push(created);
		} else {
			// Newest first, so a later batch in the hour is an earlier call: it leads the group and dates it.
			group.people.unshift(...batch.people);
			group.sentAt = batch.sentAt;
		}
	}
	return out;
};

type GroupContext = {
	t: TFunction;
	copy: OutreachCopy;
	helpers: OutreachGroupHelpers;
	signUpOf: (nurseId: number) => OutreachSignUp | undefined;
	now: DateTime;
};

const reachedRowOf = (ctx: GroupContext, entry: OutreachReachedPerson, byUser: boolean): OutreachRowView => {
	const badges = reachedBadgesOf(ctx.t, entry, byUser, ctx.signUpOf(entry.person.nurseId));
	if (entry.kind === OutreachReachedKind.Scored) return ctx.helpers.scoredRow(entry.person, badges, [], null);
	return { nurseId: entry.person.nurseId, name: entry.person.name, scored: null, line: ctx.copy.exclusionText(entry.person), badges, when: null, actions: [] };
};

const reachedGroupsOf = (ctx: GroupContext, outreach: RequestOutreach): OutreachGroupView[] => {
	const { t, helpers, now } = ctx;
	const userBatches = outreach.sentBatches.filter((batch) => batch.byUser);
	const groups: OutreachGroupView[] = [];
	if (userBatches.length > 0) {
		// Someone texted and then called is one person: keep their latest send (batches are newest first).
		const sends = userBatches.flatMap((batch) => batch.people);
		const entries = sends.filter((entry, index) => sends.findIndex((other) => other.person.nurseId === entry.person.nurseId) === index);
		const latest = userBatches[0].sentAt;
		groups.push({
			id: "sent-by-you",
			title: t("request.outreach.group.sent_by_you", { people: helpers.people(entries.length) }) + answersSummaryOf(t, entries, ctx.signUpOf),
			meta: now.diff(latest, "minutes").minutes < JUST_NOW_MINUTES ? t("request.outreach.when.just_now") : helpers.dayMeta(latest),
			icon: isCalling(outreach) ? OutreachGroupIcon.Call : OutreachGroupIcon.Message,
			note: undefined,
			rows: entries.map((entry) => reachedRowOf(ctx, entry, true)),
			countsInTab: true,
			defaultOpen: true,
		});
	}
	const byPlan = byHourOfCalls(outreach.sentBatches.filter((batch) => !batch.byUser)).map((batch): { at: DateTime; group: OutreachGroupView } => {
		const first = batch.people[0];
		const overtime = isOvertimeOffer(first);
		return {
			at: batch.sentAt,
			group: {
				id: batch.id,
				title:
					t(`request.outreach.group.sent.${overtime ? "overtime" : first.via}`, { people: helpers.people(batch.people.length) }) +
					answersSummaryOf(t, batch.people, ctx.signUpOf),
				meta: helpers.dayMeta(batch.sentAt),
				icon: overtime ? OutreachGroupIcon.Overtime : VIA_ICON[first.via],
				note: first.via === OutreachVia.AppAlert ? t("request.outreach.group.app_alert_note") : undefined,
				rows: batch.people.map((entry) => reachedRowOf(ctx, entry, false)),
				countsInTab: first.via !== OutreachVia.AppAlert,
				defaultOpen: false,
			},
		};
	});
	const { postedAt, source } = outreach.agency;
	const agency =
		postedAt === null
			? []
			: [
					{
						at: postedAt,
						group: {
							id: "agency-posted",
							title: t("request.outreach.group.agency.posted", { source: t(`request.outreach.agency.${source}`) }),
							meta: helpers.dayMeta(postedAt),
							icon: OutreachGroupIcon.Agency,
							note: t("request.outreach.group.agency.posted_note"),
							rows: [],
							countsInTab: false,
							defaultOpen: false,
						},
					},
				];
	const sorted = [...byPlan, ...agency].sort((a, b) => b.at.toMillis() - a.at.toMillis());
	return [...groups, ...sorted.map(({ group }, index) => ({ ...group, defaultOpen: index === 0 }))];
};

const notTextingGroupsOf = (ctx: GroupContext, outreach: RequestOutreach): OutreachGroupView[] => {
	const { t, helpers, copy } = ctx;
	const skipped: OutreachGroupView[] =
		outreach.skipped.length === 0
			? []
			: [
					{
						id: "skipped",
						title: t("request.outreach.group.skipped", { people: helpers.people(outreach.skipped.length) }),
						meta: undefined,
						icon: OutreachGroupIcon.Skipped,
						note: undefined,
						rows: outreach.skipped.map((person) => helpers.scoredRow(person, [], [OutreachRowAction.Undo], null)),
						countsInTab: true,
						defaultOpen: true,
					},
				];
	const excluded = outreach.excludedGroups.map(
		(group): OutreachGroupView => ({
			id: group.filter,
			title: t("request.outreach.group.excluded", {
				filter: t(`request.outreach.filter.${group.filter}`, { hours: group.limitHours }),
				people: helpers.people(group.people.length),
			}),
			meta: group.canOverride ? t("request.outreach.group.can_override") : undefined,
			icon: OutreachGroupIcon.Skipped,
			note: undefined,
			rows: group.people.map((person) => ({
				nurseId: person.nurseId,
				name: person.name,
				scored: null,
				line: copy.exclusionText(person),
				badges: [],
				when: null,
				actions: group.canOverride ? [OutreachRowAction.TextAnyway] : [],
			})),
			countsInTab: true,
			defaultOpen: false,
		}),
	);
	return [...skipped, ...excluded];
};

export const useOutreachGroups = (outreach: RequestOutreach, copy: OutreachCopy, now: DateTime): Record<OutreachTab, OutreachGroupView[]> => {
	const { t } = useTranslation();
	return useMemo(() => {
		const dayMeta = (at: DateTime): string => dayMetaOf(t, at, now);
		const rowTimeOf = (at: DateTime): string => {
			if (at.hasSame(now, "day")) return at.toFormat(TIME_FORMAT);
			if (at.hasSame(now.plus({ days: 1 }), "day")) return t("request.outreach.when.tomorrow", { time: at.toFormat(TIME_FORMAT) });
			return `${at.toFormat(ROW_DAY_FORMAT)} · ${at.toFormat(TIME_FORMAT)}`;
		};
		const scoredRow = (person: OutreachScoredPerson, badges: OutreachRowBadge[], actions: OutreachRowAction[], when: string | null): OutreachRowView => ({
			nurseId: person.nurseId,
			name: person.name,
			scored: person,
			line: null,
			badges,
			when,
			actions,
		});
		const helpers: OutreachGroupHelpers = { people: copy.people, dayMeta, rowTimeOf, scoredRow };
		const signUps = new Map(outreach.signUps.map((signUp) => [signUp.nurseId, signUp]));
		const ctx: GroupContext = { t, copy, helpers, signUpOf: (nurseId) => signUps.get(nurseId), now };
		return {
			[OutreachTab.Reached]: reachedGroupsOf(ctx, outreach),
			[OutreachTab.UpNext]: upNextGroupsOf(t, outreach, now, helpers, copy),
			[OutreachTab.NotTexting]: notTextingGroupsOf(ctx, outreach),
		};
	}, [outreach, copy, now, t]);
};
